<template>
  <div class="relation-management">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-left">
        <div class="header-info">
          <h1>关联管理</h1>
          <span class="header-desc">管理票据与银证之间的关联关系</span>
        </div>
      </div>
      <div class="header-stats">
        <span class="stat-item">总关联: {{ totalRelations }}</span>
        <span class="stat-item">今日新增: {{ todayRelations }}</span>
        <span class="stat-item">待处理: {{ pendingRelations }}</span>
      </div>
    </div>

    <!-- 功能标签页 -->
    <div class="tab-navigation">
      <div class="tab-container">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          @click="activeTab = tab.key"
          :class="['tab-button', { active: activeTab === tab.key }]"
        >
          <i :class="tab.icon"></i>
          <span>{{ tab.title }}</span>
        </button>
      </div>
    </div>

    <!-- 内容区域 -->
    <div class="content-area">
      <!-- 创建关联 -->
      <div v-if="activeTab === 'create'" class="tab-content">
        <div class="create-relation-container">
          <div class="form-header">
            <h2>创建新关联</h2>
            <p>选择银行回单和票据建立关联关系</p>
          </div>

          <div class="relation-form">
            <!-- 银行回单选择区域 -->
            <div class="selection-section">
              <div class="section-header">
                <h3>
                  <i class="fa fa-university"></i>
                  银行回单选择
                </h3>
                <div class="selection-mode">
                  <label class="radio-option">
                    <input type="radio" v-model="receiptSelectionMode" value="single" />
                    <span>单张回单</span>
                  </label>
                  <label class="radio-option">
                    <input type="radio" v-model="receiptSelectionMode" value="group" />
                    <span>回单组合</span>
                  </label>
                </div>
              </div>

              <!-- 搜索控件 -->
              <div class="search-controls">
                <div class="search-input-group">
                  <i class="fa fa-search"></i>
                  <input
                    v-model="receiptSearchKeyword"
                    placeholder="搜索回单编号、交易方、金额..."
                    class="search-input"
                    @input="searchReceipts"
                  />
                </div>
                <button @click="searchReceipts" class="btn secondary">
                  <i class="fa fa-search"></i> 搜索
                </button>
                <button @click="loadRecommendedReceipts" class="btn primary">
                  <i class="fa fa-magic"></i> 智能推荐
                </button>
              </div>

              <!-- 回单列表 -->
              <div class="items-grid">
                <!-- 单张回单模式 -->
                <div
                  v-if="receiptSelectionMode === 'single'"
                  v-for="receipt in filteredReceipts"
                  :key="receipt.id"
                  @click="handleClickReceipt(receipt)"
                  :class="['item-card receipt-card', { selected: selectedReceiptId === receipt.id, associated: isReceiptAssociated(receipt.id) }]"
                >
                  <div class="card-header">
                    <div class="item-title">
                      <span class="item-number">{{ receipt.receiptNo || receipt.serialNumber }}</span>
                      <span class="item-amount">¥{{ formatAmount(receipt.amount) }}</span>
                    </div>
                    <div class="card-flags">
                      <span v-if="isReceiptAssociated(receipt.id)" class="associated-flag"><i class="fa fa-link"></i> 已关联</span>
                      <span class="selection-indicator"><i class="fa fa-check-circle"></i></span>
                    </div>
                  </div>

                  <div class="card-content">
                    <div class="item-info">
                      <div class="info-row">
                        <label>日期:</label>
                        <span>{{ formatDate(receipt.receiptsDate || receipt.transferDate) }}</span>
                      </div>
                      <div class="info-row">
                        <label>付款方:</label>
                        <span>{{ receipt.payerName || '未知' }}</span>
                      </div>
                      <div class="info-row">
                        <label>收款方:</label>
                        <span>{{ receipt.payeeName || '未知' }}</span>
                      </div>
                      <div class="info-row">
                        <label>摘要:</label>
                        <span>{{ receipt.summary || '无' }}</span>
                      </div>
                    </div>

                    <div v-if="receipt.filePath" class="item-thumbnail">
                      <img :src="receipt.filePath" :alt="receipt.receiptNo" @click.stop="showImagePreview(receipt.filePath, receipt.receiptNo)" />
                    </div>
                  </div>
                </div>

                <!-- 回单组合模式 -->
                <div
                  v-if="receiptSelectionMode === 'group'"
                  v-for="group in filteredReceipts"
                  :key="group.groupId"
                  @click="handleClickReceiptGroup(group)"
                  :class="['item-card receipt-group-card', { selected: selectedReceiptGroupId === group.groupId, associated: isReceiptGroupAssociated(group.groupId) }]"
                >
                  <div class="card-header">
                    <div class="item-title">
                      <span class="item-number">{{ group.groupName }}</span>
                      <span class="item-amount">¥{{ formatAmount(group.totalAmount) }}</span>
                    </div>
                    <div class="selection-indicator">
                      <i class="fa fa-check-circle"></i>
                    </div>
                  </div>

                      <div class="card-flags">
                        <span v-if="isReceiptGroupAssociated(group.groupId)" class="associated-flag"><i class="fa fa-link"></i> 已关联</span>
                      </div>
                  <div class="card-content">
                    <div class="item-info">
                      <div class="info-row">
                        <label>创建日期:</label>
                        <span>{{ formatDate(group.createdAt) }}</span>
                      </div>
                      <div class="info-row">
                        <label>包含回单:</label>
                        <span>{{ group.itemCount || 0 }} 张</span>
                      </div>
                      <div class="info-row">
                        <label>状态:</label>
                        <span class="status-active">已归并</span>
                      </div>
                      <div class="info-row">
                        <label>备注:</label>
                        <span>{{ group.remark || '无' }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 银行回单分页 -->
              <div class="pagination-container">
                <div class="pagination-info">
                  <span>共 {{ receiptPagination.total }} 条记录</span>
                  <div class="page-size-selector">
                    <label>每页显示:</label>
                    <select
                      v-model="receiptPagination.pageSize"
                      @change="handleReceiptPageSizeChange(receiptPagination.pageSize)"
                      class="page-size-select"
                    >
                      <option v-for="size in pageSizeOptions" :key="size" :value="size">{{ size }}</option>
                    </select>
                  </div>
                </div>

                <div class="pagination-controls">
                  <button
                    @click="handleReceiptPageChange(receiptPagination.currentPage - 1)"
                    :disabled="receiptPagination.currentPage <= 1"
                    class="pagination-btn"
                  >
                    <i class="fa fa-chevron-left"></i>
                  </button>

                  <div class="page-numbers">
                    <button
                      v-for="page in getPageNumbers(receiptPagination)"
                      :key="page"
                      @click="handleReceiptPageChange(page)"
                      :class="['page-number', { active: page === receiptPagination.currentPage }]"
                    >
                      {{ page }}
                    </button>
                  </div>

                  <button
                    @click="handleReceiptPageChange(receiptPagination.currentPage + 1)"
                    :disabled="receiptPagination.currentPage >= Math.ceil(receiptPagination.total / receiptPagination.pageSize)"
                    class="pagination-btn"
                  >
                    <i class="fa fa-chevron-right"></i>
                  </button>
                </div>
              </div>
            </div>

            <!-- 票据选择区域 -->
            <div class="selection-section">
              <div class="section-header">
                <h3>
                  <i class="fa fa-file-text"></i>
                  票据选择
                </h3>
                <div class="selection-mode">
                  <label class="radio-option">
                    <input type="radio" v-model="billSelectionMode" value="single" />
                    <span>单张票据</span>
                  </label>
                  <label class="radio-option">
                    <input type="radio" v-model="billSelectionMode" value="group" />
                    <span>票据组合</span>
                  </label>
                </div>
              </div>

              <!-- 搜索控件 -->
              <div class="search-controls">
                <div class="search-input-group">
                  <i class="fa fa-search"></i>
                  <input
                    v-model="billSearchKeyword"
                    placeholder="搜索票据编号、开票方、金额..."
                    class="search-input"
                    @input="searchBills"
                  />
                </div>
                <button @click="searchBills" class="btn secondary">
                  <i class="fa fa-search"></i> 搜索
                </button>
                <button @click="loadRecommendedBills" class="btn primary">
                  <i class="fa fa-magic"></i> 智能推荐
                </button>
              </div>

              <!-- 票据列表 -->
              <div class="items-grid">
                <!-- 单张票据模式 -->
                <div
                  v-if="billSelectionMode === 'single'"
                  v-for="bill in filteredBills"
                  :key="bill.id"
                  @click="handleClickBill(bill)"
                  :class="['item-card bill-card', { selected: selectedBillId === bill.id, associated: isBillAssociated(bill.id) }]"
                >
                  <div class="card-header">
                    <div class="item-title">
                      <span class="item-number">{{ bill.billNo || bill.invoiceNumber }}</span>
                      <span class="item-amount">¥{{ formatAmount(bill.amount) }}</span>
                    </div>
                    <div class="card-flags">
                      <span v-if="isBillAssociated(bill.id)" class="associated-flag"><i class="fa fa-link"></i> 已关联</span>
                      <span class="selection-indicator"><i class="fa fa-check-circle"></i></span>
                    </div>
                  </div>

                  <div class="card-content">
                    <div class="item-info">
                      <div class="info-row">
                        <label>日期:</label>
                        <span>{{ formatDate(bill.billDate) }}</span>
                      </div>
                      <div class="info-row">
                        <label>开票方:</label>
                        <span>{{ bill.issuer || '未知' }}</span>
                      </div>
                      <div class="info-row">
                        <label>收票方:</label>
                        <span>{{ bill.recipient || '未知' }}</span>
                      </div>
                      <div class="info-row">
                        <label>摘要:</label>
                        <span>{{ bill.summary || '无' }}</span>
                      </div>
                    </div>

                    <div v-if="bill.attachmentPath" class="item-thumbnail">
                      <img :src="bill.attachmentPath" :alt="bill.billNo" @click.stop="showImagePreview(bill.attachmentPath, bill.billNo)" />
                    </div>
                  </div>
                </div>

                <!-- 票据组合模式 -->
                <div
                  v-if="billSelectionMode === 'group'"
                  v-for="group in filteredBills"
                  :key="group.groupId"
                  @click="handleClickBillGroup(group)"
                  :class="['item-card bill-group-card', { selected: selectedBillGroupId === group.groupId, associated: isBillGroupAssociated(group.groupId) }]"
                >
                  <div class="card-header">
                    <div class="item-title">
                      <span class="item-number">{{ group.groupName }}</span>
                      <span class="item-amount">¥{{ formatAmount(group.totalAmount) }}</span>
                    </div>
                    <div class="selection-indicator">
                      <i class="fa fa-check-circle"></i>
                    </div>
                  </div>

                      <div class="card-flags">
                        <span v-if="isBillGroupAssociated(group.groupId)" class="associated-flag"><i class="fa fa-link"></i> 已关联</span>
                      </div>
                  <div class="card-content">
                    <div class="item-info">
                      <div class="info-row">
                        <label>创建日期:</label>
                        <span>{{ formatDate(group.createdAt) }}</span>
                      </div>
                      <div class="info-row">
                        <label>包含票据:</label>
                        <span>{{ group.itemCount || 0 }} 张</span>
                      </div>
                      <div class="info-row">
                        <label>状态:</label>
                        <span class="status-active">已归并</span>
                      </div>
                      <div class="info-row">
                        <label>备注:</label>
                        <span>{{ group.remark || '无' }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 票据分页 -->
              <div class="pagination-container">
                <div class="pagination-info">
                  <span>共 {{ billPagination.total }} 条记录</span>
                  <div class="page-size-selector">
                    <label>每页显示:</label>
                    <select
                      v-model="billPagination.pageSize"
                      @change="handleBillPageSizeChange(billPagination.pageSize)"
                      class="page-size-select"
                    >
                      <option v-for="size in pageSizeOptions" :key="size" :value="size">{{ size }}</option>
                    </select>
                  </div>
                </div>

                <div class="pagination-controls">
                  <button
                    @click="handleBillPageChange(billPagination.currentPage - 1)"
                    :disabled="billPagination.currentPage <= 1"
                    class="pagination-btn"
                  >
                    <i class="fa fa-chevron-left"></i>
                  </button>

                  <div class="page-numbers">
                    <button
                      v-for="page in getPageNumbers(billPagination)"
                      :key="page"
                      @click="handleBillPageChange(page)"
                      :class="['page-number', { active: page === billPagination.currentPage }]"
                    >
                      {{ page }}
                    </button>
                  </div>

                  <button
                    @click="handleBillPageChange(billPagination.currentPage + 1)"
                    :disabled="billPagination.currentPage >= Math.ceil(billPagination.total / billPagination.pageSize)"
                    class="pagination-btn"
                  >
                    <i class="fa fa-chevron-right"></i>
                  </button>
                </div>
              </div>
            </div>

            <!-- 关联设置 -->
            <div class="relation-settings">
              <div class="settings-header">
                <h3>
                  <i class="fa fa-link"></i>
                  关联设置
                </h3>
              </div>

              <div class="settings-form">
                <div class="form-group">
                  <label>关联类型:</label>
                  <div class="radio-group">
                    <label class="radio-option">
                      <input type="radio" v-model="relationForm.relationType" value="FULL" />
                      <span>完全关联</span>
                    </label>
                    <label class="radio-option">
                      <input type="radio" v-model="relationForm.relationType" value="PARTIAL" />
                      <span>部分关联</span>
                    </label>
                  </div>
                </div>

                <div v-if="relationForm.relationType === 'PARTIAL'" class="form-group">
                  <label>关联金额:</label>
                  <input
                    type="number"
                    v-model="relationForm.relationAmount"
                    placeholder="请输入关联金额"
                    class="form-input"
                  />
                </div>

                <div class="form-group">
                  <label>备注说明:</label>
                  <textarea
                    v-model="relationForm.relationNote"
                    placeholder="请输入关联说明..."
                    class="form-textarea"
                    rows="3"
                  ></textarea>
                </div>

                <div class="form-actions">
                  <button @click="resetForm" class="btn secondary">
                    <i class="fa fa-refresh"></i> 重置
                  </button>
                  <button @click="createRelation" class="btn primary" :disabled="!canCreateRelation">
                    <i class="fa fa-link"></i> 创建关联
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 关联列表 -->
      <div v-if="activeTab === 'list'" class="tab-content">
        <div class="relation-list-container">
          <div class="list-header">
            <h2>关联列表</h2>
            <p>查看和管理已建立的关联关系</p>
          </div>

          <div class="list-controls">
            <div class="search-input-group">
              <i class="fa fa-search"></i>
              <input
                v-model="relationSearchKeyword"
                placeholder="搜索关联关系..."
                class="search-input"
                @input="searchRelations"
              />
            </div>
            <button @click="loadRelations" class="btn secondary">
              <i class="fa fa-refresh"></i> 刷新
            </button>
          </div>

          <div class="relations-grid">
            <div
              v-for="relation in filteredRelations"
              :key="relation.relationId"
              class="relation-card"
            >
              <div class="relation-header">
                <div class="relation-type">
                  <i class="fa fa-link"></i>
                  <span>{{ getRelationTypeText(relation.relationType) }}</span>
                </div>
                <div class="relation-actions">
                  <button @click="showRelationDetail(relation)" class="action-btn">
                    <i class="fa fa-eye"></i>
                  </button>
                  <button @click="deleteRelation(relation.relationId)" class="action-btn danger">
                    <i class="fa fa-trash"></i>
                  </button>
                </div>
              </div>

              <div class="relation-content">
                <div class="relation-entities">
                  <div class="entity source">
                    <div class="entity-type">{{ getEntityTypeText(relation.sourceType) }}</div>
                    <div class="entity-id">{{ relation.sourceId }}</div>
                  </div>
                  <div class="relation-arrow">
                    <i class="fa fa-arrow-right"></i>
                  </div>
                  <div class="entity target">
                    <div class="entity-type">{{ getEntityTypeText(relation.targetType) }}</div>
                    <div class="entity-id">{{ relation.targetId }}</div>
                  </div>
                </div>

                <div class="relation-info">
                  <div class="info-row">
                    <label>创建时间:</label>
                    <span>{{ formatDate(relation.createdAt) }}</span>
                  </div>
                  <div v-if="relation.relationAmount" class="info-row">
                    <label>关联金额:</label>
                    <span>¥{{ formatAmount(relation.relationAmount) }}</span>
                  </div>
                  <div v-if="relation.relationNote" class="info-row">
                    <label>备注:</label>
                    <span>{{ relation.relationNote }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 关联关系分页 -->
          <div class="pagination-container">
            <div class="pagination-info">
              <span>共 {{ relationPagination.total }} 条记录</span>
              <div class="page-size-selector">
                <label>每页显示:</label>
                <select
                  v-model="relationPagination.pageSize"
                  @change="handleRelationPageSizeChange(relationPagination.pageSize)"
                  class="page-size-select"
                >
                  <option v-for="size in pageSizeOptions" :key="size" :value="size">{{ size }}</option>
                </select>
              </div>
            </div>

            <div class="pagination-controls">
              <button
                @click="handleRelationPageChange(relationPagination.currentPage - 1)"
                :disabled="relationPagination.currentPage <= 1"
                class="pagination-btn"
              >
                <i class="fa fa-chevron-left"></i>
              </button>

              <div class="page-numbers">
                <button
                  v-for="page in getPageNumbers(relationPagination)"
                  :key="page"
                  @click="handleRelationPageChange(page)"
                  :class="['page-number', { active: page === relationPagination.currentPage }]"
                >
                  {{ page }}
                </button>
              </div>

              <button
                @click="handleRelationPageChange(relationPagination.currentPage + 1)"
                :disabled="relationPagination.currentPage >= Math.ceil(relationPagination.total / relationPagination.pageSize)"
                class="pagination-btn"
              >
                <i class="fa fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 批量关联 -->
      <div v-if="activeTab === 'batch'" class="tab-content">
        <div class="batch-relation-container">
          <div class="batch-header">
            <h2>批量关联</h2>
            <p>批量创建多个关联关系，提升工作效率</p>
          </div>

          <div class="batch-content">
            <!-- 批量关联模式选择 -->
            <div class="batch-mode-selection">
              <h3>批量关联模式</h3>
              <div class="mode-options">
                <label class="mode-option" :class="{ active: batchMode === 'auto' }">
                  <input type="radio" v-model="batchMode" value="auto" />
                  <div class="option-content">
                    <i class="fa fa-magic"></i>
                    <span class="option-title">智能匹配</span>
                    <span class="option-desc">基于金额、日期、摘要等信息自动匹配</span>
                  </div>
                </label>

                <label class="mode-option" :class="{ active: batchMode === 'manual' }">
                  <input type="radio" v-model="batchMode" value="manual" />
                  <div class="option-content">
                    <i class="fa fa-hand-pointer-o"></i>
                    <span class="option-title">手动选择</span>
                    <span class="option-desc">手动选择多个银行回单和票据进行批量关联</span>
                  </div>
                </label>

                <label class="mode-option" :class="{ active: batchMode === 'import' }">
                  <input type="radio" v-model="batchMode" value="import" />
                  <div class="option-content">
                    <i class="fa fa-upload"></i>
                    <span class="option-title">文件导入</span>
                    <span class="option-desc">通过Excel文件批量导入关联关系</span>
                  </div>
                </label>
              </div>
            </div>

            <!-- 智能匹配模式 -->
            <div v-if="batchMode === 'auto'" class="batch-auto-mode">
              <div class="auto-settings">
                <h4>匹配规则设置</h4>
                <div class="settings-grid">
                  <div class="setting-item">
                    <label>
                      <input type="checkbox" v-model="autoMatchSettings.byAmount" />
                      金额匹配
                    </label>
                    <div v-if="autoMatchSettings.byAmount" class="sub-setting">
                      <label>容差范围:</label>
                      <input type="number" v-model="autoMatchSettings.amountTolerance" min="0" step="0.01" />
                      <span>元</span>
                    </div>
                  </div>

                  <div class="setting-item">
                    <label>
                      <input type="checkbox" v-model="autoMatchSettings.byDate" />
                      日期匹配
                    </label>
                    <div v-if="autoMatchSettings.byDate" class="sub-setting">
                      <label>日期范围:</label>
                      <select v-model="autoMatchSettings.dateRange">
                        <option value="0">同一天</option>
                        <option value="1">±1天</option>
                        <option value="3">±3天</option>
                        <option value="7">±7天</option>
                      </select>
                    </div>
                  </div>

                  <div class="setting-item">
                    <label>
                      <input type="checkbox" v-model="autoMatchSettings.bySummary" />
                      摘要匹配
                    </label>
                    <div v-if="autoMatchSettings.bySummary" class="sub-setting">
                      <label>相似度:</label>
                      <select v-model="autoMatchSettings.summaryThreshold">
                        <option value="0.5">50%</option>
                        <option value="0.7">70%</option>
                        <option value="0.8">80%</option>
                        <option value="0.9">90%</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div class="auto-actions">
                  <button @click="startAutoMatch" class="btn primary" :disabled="autoMatching">
                    <i class="fa fa-play"></i>
                    {{ autoMatching ? '匹配中...' : '开始智能匹配' }}
                  </button>
                  <button @click="clearAutoResults" class="btn secondary">
                    <i class="fa fa-refresh"></i>
                    清空结果
                  </button>
                </div>
              </div>

              <!-- 匹配结果 -->
              <div v-if="autoMatchResults.length > 0" class="match-results">
                <h4>匹配结果 ({{ autoMatchResults.length }}条)</h4>
                <div class="results-list">
                  <div v-for="(result, index) in autoMatchResults" :key="index" class="match-result-item">
                    <div class="result-header">
                      <label class="result-checkbox">
                        <input type="checkbox" v-model="result.selected" />
                        <span>匹配度: {{ (result.confidence * 100).toFixed(1) }}%</span>
                      </label>
                      <div class="result-actions">
                        <button @click="viewMatchDetail(result)" class="action-btn">
                          <i class="fa fa-eye"></i>
                        </button>
                        <button @click="removeMatchResult(index)" class="action-btn danger">
                          <i class="fa fa-times"></i>
                        </button>
                      </div>
                    </div>

                    <div class="result-content">
                      <div class="match-pair">
                        <div class="match-source">
                          <div class="entity-type">银行回单</div>
                          <div class="entity-info">
                            <div class="entity-id">{{ result.receipt.receiptNo }}</div>
                            <div class="entity-amount">¥{{ formatAmount(result.receipt.amount) }}</div>
                            <div class="entity-date">{{ formatDate(result.receipt.receiptsDate) }}</div>
                          </div>
                        </div>

                        <div class="match-arrow">
                          <i class="fa fa-arrow-right"></i>
                        </div>

                        <div class="match-target">
                          <div class="entity-type">票据</div>
                          <div class="entity-info">
                            <div class="entity-id">{{ result.bill.billNo }}</div>
                            <div class="entity-amount">¥{{ formatAmount(result.bill.amount) }}</div>
                            <div class="entity-date">{{ formatDate(result.bill.billDate) }}</div>
                          </div>
                        </div>
                      </div>

                      <div class="match-reasons">
                        <span v-for="reason in result.reasons" :key="reason" class="reason-tag">
                          {{ reason }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="batch-actions">
                  <button @click="selectAllMatches" class="btn secondary">
                    <i class="fa fa-check-square"></i>
                    全选
                  </button>
                  <button @click="deselectAllMatches" class="btn secondary">
                    <i class="fa fa-square-o"></i>
                    取消全选
                  </button>
                  <button @click="createBatchRelations" class="btn primary" :disabled="!hasSelectedMatches">
                    <i class="fa fa-link"></i>
                    创建选中关联 ({{ selectedMatchesCount }})
                  </button>
                </div>
              </div>
            </div>

            <!-- 手动选择模式 -->
            <div v-if="batchMode === 'manual'" class="batch-manual-mode">
              <div class="manual-info">
                <i class="fa fa-info-circle"></i>
                <span>手动选择模式：请先选择多个银行回单，然后选择对应的票据，系统将为您批量创建关联关系。</span>
              </div>

              <div class="manual-selection">
                <div class="selection-summary">
                  <div class="summary-item">
                    <span class="summary-label">已选银行回单:</span>
                    <span class="summary-count">{{ selectedReceipts.length }}</span>
                  </div>
                  <div class="summary-item">
                    <span class="summary-label">已选票据:</span>
                    <span class="summary-count">{{ selectedBills.length }}</span>
                  </div>
                </div>

                <div class="manual-actions">
                  <button @click="clearManualSelection" class="btn secondary">
                    <i class="fa fa-refresh"></i>
                    清空选择
                  </button>
                  <button @click="createManualRelations" class="btn primary" :disabled="!canCreateManualRelations">
                    <i class="fa fa-link"></i>
                    创建批量关联
                  </button>
                </div>
              </div>
            </div>

            <!-- 文件导入模式 -->
            <div v-if="batchMode === 'import'" class="batch-import-mode">
              <div class="import-info">
                <i class="fa fa-info-circle"></i>
                <span>支持Excel文件导入，请按照模板格式准备数据。</span>
              </div>

              <div class="import-actions">
                <button @click="downloadTemplate" class="btn secondary">
                  <i class="fa fa-download"></i>
                  下载模板
                </button>
                <button @click="selectImportFile" class="btn primary">
                  <i class="fa fa-upload"></i>
                  选择文件
                </button>
              </div>

              <input ref="fileInput" type="file" accept=".xlsx,.xls" @change="handleFileImport" style="display: none;" />

              <div v-if="importResults.length > 0" class="import-results">
                <h4>导入结果</h4>
                <div class="results-summary">
                  <span class="success-count">成功: {{ importResults.filter(r => r.success).length }}</span>
                  <span class="error-count">失败: {{ importResults.filter(r => !r.success).length }}</span>
                </div>

                <div class="results-details">
                  <div v-for="(result, index) in importResults" :key="index"
                       :class="['result-item', result.success ? 'success' : 'error']">
                    <div class="result-info">
                      <span class="result-index">{{ index + 1 }}</span>
                      <span class="result-message">{{ result.message }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- AI智能关联 -->
      <div v-if="activeTab === 'ai'" class="tab-content">
        <div class="ai-relation-container">
          <div class="ai-header">
            <h2>AI智能关联</h2>
            <p>使用人工智能自动识别和建立关联关系</p>
          </div>

          <div class="ai-content">
            <!-- AI配置 -->
            <div class="ai-config-section">
              <h3>AI配置</h3>
              <div class="config-grid">
                <div class="config-item">
                  <label>AI模型:</label>
                  <select v-model="aiConfig.model" class="config-select">
                    <option v-for="model in availableModels" :key="model.id" :value="model.id">
                      {{ model.name }}
                    </option>
                  </select>
                </div>

                <div class="config-item">
                  <label>匹配精度:</label>
                  <select v-model="aiConfig.precision" class="config-select">
                    <option value="high">高精度 (90%+)</option>
                    <option value="medium">中等精度 (70%+)</option>
                    <option value="low">低精度 (50%+)</option>
                  </select>
                </div>

                <div class="config-item">
                  <label>分析范围:</label>
                  <select v-model="aiConfig.scope" class="config-select">
                    <option value="all">全部数据</option>
                    <option value="recent">最近30天</option>
                    <option value="current">当前月份</option>
                  </select>
                </div>

                <div class="config-item">
                  <label>自动创建:</label>
                  <div class="checkbox-group">
                    <label class="checkbox-option">
                      <input type="checkbox" v-model="aiConfig.autoCreate" />
                      <span>自动创建高置信度关联</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- AI分析控制 -->
            <div class="ai-control-section">
              <h3>AI分析控制</h3>
              <div class="control-panel">
                <div class="analysis-stats">
                  <div class="stat-item">
                    <span class="stat-label">待分析银行回单:</span>
                    <span class="stat-value">{{ unanalyzedReceipts }}</span>
                  </div>
                  <div class="stat-item">
                    <span class="stat-label">待分析票据:</span>
                    <span class="stat-value">{{ unanalyzedBills }}</span>
                  </div>
                  <div class="stat-item">
                    <span class="stat-label">AI分析进度:</span>
                    <span class="stat-value">{{ aiAnalysisProgress }}%</span>
                  </div>
                </div>

                <div class="control-actions">
                  <button @click="startAIAnalysis" class="btn primary" :disabled="aiAnalyzing">
                    <i class="fa fa-play"></i>
                    {{ aiAnalyzing ? 'AI分析中...' : '开始AI分析' }}
                  </button>
                  <button @click="pauseAIAnalysis" class="btn secondary" :disabled="!aiAnalyzing">
                    <i class="fa fa-pause"></i>
                    暂停分析
                  </button>
                  <button @click="stopAIAnalysis" class="btn danger" :disabled="!aiAnalyzing">
                    <i class="fa fa-stop"></i>
                    停止分析
                  </button>
                </div>
              </div>

              <!-- 分析进度条 -->
              <div v-if="aiAnalyzing" class="progress-section">
                <div class="progress-bar">
                  <div class="progress-fill" :style="{ width: aiAnalysisProgress + '%' }"></div>
                </div>
                <div class="progress-info">
                  <span>{{ aiCurrentTask }}</span>
                  <span>{{ aiAnalysisProgress }}% ({{ aiProcessedCount }}/{{ aiTotalCount }})</span>
                </div>
              </div>
            </div>

            <!-- AI分析结果 -->
            <div v-if="aiResults.length > 0" class="ai-results-section">
              <h3>AI分析结果</h3>

              <div class="results-summary">
                <div class="summary-stats">
                  <div class="summary-item">
                    <span class="summary-label">发现关联:</span>
                    <span class="summary-value">{{ aiResults.length }}</span>
                  </div>
                  <div class="summary-item">
                    <span class="summary-label">高置信度:</span>
                    <span class="summary-value">{{ highConfidenceResults.length }}</span>
                  </div>
                  <div class="summary-item">
                    <span class="summary-label">中等置信度:</span>
                    <span class="summary-value">{{ mediumConfidenceResults.length }}</span>
                  </div>
                  <div class="summary-item">
                    <span class="summary-label">低置信度:</span>
                    <span class="summary-value">{{ lowConfidenceResults.length }}</span>
                  </div>
                </div>

                <div class="results-actions">
                  <button @click="selectHighConfidence" class="btn secondary">
                    <i class="fa fa-check"></i>
                    选择高置信度
                  </button>
                  <button @click="selectAllAIResults" class="btn secondary">
                    <i class="fa fa-check-square"></i>
                    全选
                  </button>
                  <button @click="createSelectedAIRelations" class="btn primary" :disabled="!hasSelectedAIResults">
                    <i class="fa fa-link"></i>
                    创建选中关联 ({{ selectedAIResultsCount }})
                  </button>
                </div>
              </div>

              <div class="results-list">
                <div v-for="(result, index) in aiResults" :key="index" class="ai-result-item">
                  <div class="result-header">
                    <label class="result-checkbox">
                      <input type="checkbox" v-model="result.selected" />
                      <span class="confidence-badge" :class="getConfidenceClass(result.confidence)">
                        {{ (result.confidence * 100).toFixed(1) }}%
                      </span>
                    </label>
                    <div class="result-actions">
                      <button @click="viewAIResultDetail(result)" class="action-btn">
                        <i class="fa fa-eye"></i>
                      </button>
                      <button @click="removeAIResult(index)" class="action-btn danger">
                        <i class="fa fa-times"></i>
                      </button>
                    </div>
                  </div>

                  <div class="result-content">
                    <div class="ai-match-pair">
                      <div class="match-source">
                        <div class="entity-type">银行回单</div>
                        <div class="entity-info">
                          <div class="entity-id">{{ result.receipt.receiptNo }}</div>
                          <div class="entity-amount">¥{{ formatAmount(result.receipt.amount) }}</div>
                          <div class="entity-date">{{ formatDate(result.receipt.receiptsDate) }}</div>
                        </div>
                      </div>

                      <div class="match-arrow">
                        <i class="fa fa-arrow-right"></i>
                      </div>

                      <div class="match-target">
                        <div class="entity-type">票据</div>
                        <div class="entity-info">
                          <div class="entity-id">{{ result.bill.billNo }}</div>
                          <div class="entity-amount">¥{{ formatAmount(result.bill.amount) }}</div>
                          <div class="entity-date">{{ formatDate(result.bill.billDate) }}</div>
                        </div>
                      </div>
                    </div>

                    <div class="ai-analysis">
                      <div class="analysis-reasons">
                        <h5>AI分析依据:</h5>
                        <ul>
                          <li v-for="reason in result.analysisReasons" :key="reason">{{ reason }}</li>
                        </ul>
                      </div>

                      <div class="analysis-suggestion">
                        <h5>AI建议:</h5>
                        <p>{{ result.suggestion }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- AI学习反馈 -->
            <div class="ai-feedback-section">
              <h3>AI学习反馈</h3>
              <div class="feedback-info">
                <i class="fa fa-lightbulb-o"></i>
                <span>您的反馈将帮助AI模型不断改进匹配精度。请对AI的关联建议进行评价。</span>
              </div>

              <div class="feedback-stats">
                <div class="feedback-item">
                  <span class="feedback-label">正确关联:</span>
                  <span class="feedback-value">{{ aiFeedback.correct }}</span>
                </div>
                <div class="feedback-item">
                  <span class="feedback-label">错误关联:</span>
                  <span class="feedback-value">{{ aiFeedback.incorrect }}</span>
                </div>
                <div class="feedback-item">
                  <span class="feedback-label">准确率:</span>
                  <span class="feedback-value">{{ aiAccuracyRate }}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 图片预览浮窗 -->
    <div v-if="showImageModal" class="image-modal" @click="closeImageModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h4>{{ currentImageTitle }}</h4>
          <button class="close-btn" @click="closeImageModal">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <img :src="currentImageUrl" :alt="currentImageTitle" class="preview-image" />
        </div>
        <div class="modal-footer">
          <button class="btn secondary" @click="closeImageModal">关闭</button>
          <a :href="currentImageUrl" target="_blank" class="btn primary">
            <i class="fa fa-external-link"></i> 在新窗口打开
          </a>
        </div>
      </div>
    </div>

    <!-- 关联详情浮窗 -->
    <div v-if="showDetailModal" class="detail-modal" @click="closeDetailModal">
      <div class="modal-content large" @click.stop>
        <div class="modal-header">
          <h4>关联详情</h4>
          <button class="close-btn" @click="closeDetailModal">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="detail-content">
            <!-- 基本信息 -->
            <div class="detail-section">
              <h5>关联信息</h5>
              <div class="detail-grid">
                <div class="detail-item">
                  <label>关联类型:</label>
                  <span>{{ selectedRelation && getRelationTypeText(selectedRelation.relationType) }}</span>
                </div>
                <div class="detail-item">
                  <label>创建时间:</label>
                  <span>{{ selectedRelation && formatDate(selectedRelation.createdAt) }}</span>
                </div>
                <div v-if="selectedRelation && selectedRelation.relationAmount" class="detail-item">
                  <label>关联金额:</label>
                  <span class="amount">¥{{ selectedRelation && formatAmount(selectedRelation.relationAmount) }}</span>
                </div>
                <div v-if="selectedRelation && selectedRelation.relationNote" class="detail-item full-width">
                  <label>备注说明:</label>
                  <span>{{ selectedRelation && selectedRelation.relationNote }}</span>
                </div>
              </div>
            </div>

            <!-- 源实体信息 -->
            <div class="detail-section" v-if="selectedRelation">
              <h5>源实体信息</h5>
              <div class="entity-card">
                <div class="entity-header">
                  <span class="entity-type">{{ getEntityTypeText(selectedRelation.sourceType) }}</span>
                  <span class="entity-id">ID: {{ selectedRelation.sourceId }}</span>
                </div>
                <div class="entity-details" v-if="sourceEntityDetail && !sourceEntityDetail.error">
                  <!-- 票据信息 -->
                  <template v-if="selectedRelation.sourceType === 'DOCUMENT'">
                    <div class="detail-row" v-if="sourceEntityDetail.billNo">
                      <label>票据编号:</label>
                      <span>{{ sourceEntityDetail.billNo }}</span>
                    </div>
                    <div class="detail-row" v-if="sourceEntityDetail.amount">
                      <label>金额:</label>
                      <span class="amount">¥{{ formatAmount(sourceEntityDetail.amount) }}</span>
                    </div>
                    <div class="detail-row" v-if="sourceEntityDetail.billDate">
                      <label>票据日期:</label>
                      <span>{{ formatDate(sourceEntityDetail.billDate) }}</span>
                    </div>
                    <div class="detail-row" v-if="sourceEntityDetail.issuer">
                      <label>开票方:</label>
                      <span>{{ sourceEntityDetail.issuer }}</span>
                    </div>
                    <div class="detail-row" v-if="sourceEntityDetail.summary">
                      <label>摘要:</label>
                      <span>{{ sourceEntityDetail.summary }}</span>
                    </div>
                  </template>

                  <!-- 银行回单信息 -->
                  <template v-else-if="selectedRelation.sourceType === 'RECEIPT'">
                    <div class="detail-row" v-if="sourceEntityDetail.receiptsNo">
                      <label>回单编号:</label>
                      <span>{{ sourceEntityDetail.receiptsNo }}</span>
                    </div>
                    <div class="detail-row" v-if="sourceEntityDetail.amount">
                      <label>金额:</label>
                      <span class="amount">¥{{ formatAmount(sourceEntityDetail.amount) }}</span>
                    </div>
                    <div class="detail-row" v-if="sourceEntityDetail.receiptsDate">
                      <label>回单日期:</label>
                      <span>{{ formatDate(sourceEntityDetail.receiptsDate) }}</span>
                    </div>
                    <div class="detail-row" v-if="sourceEntityDetail.payeeName">
                      <label>收款人:</label>
                      <span>{{ sourceEntityDetail.payeeName }}</span>
                    </div>
                    <div class="detail-row" v-if="sourceEntityDetail.payerName">
                      <label>付款人:</label>
                      <span>{{ sourceEntityDetail.payerName }}</span>
                    </div>
                    <div class="detail-row" v-if="sourceEntityDetail.summary">
                      <label>摘要:</label>
                      <span>{{ sourceEntityDetail.summary }}</span>
                    </div>
                  </template>

                  <!-- 归并组信息 -->
                  <template v-else-if="selectedRelation.sourceType === 'DOCUMENT_GROUP' || selectedRelation.sourceType === 'RECEIPT_GROUP'">
                    <div class="detail-row" v-if="sourceEntityDetail.groupName">
                      <label>组名称:</label>
                      <span>{{ sourceEntityDetail.groupName }}</span>
                    </div>
                    <div class="detail-row" v-if="sourceEntityDetail.totalAmount">
                      <label>总金额:</label>
                      <span class="amount">¥{{ formatAmount(sourceEntityDetail.totalAmount) }}</span>
                    </div>
                    <div class="detail-row" v-if="sourceEntityDetail.itemCount">
                      <label>包含项目:</label>
                      <span>{{ sourceEntityDetail.itemCount }} 项</span>
                    </div>
                  </template>
                </div>
                <div v-else-if="sourceEntityDetail && sourceEntityDetail.error" class="error-text">
                  加载失败: {{ sourceEntityDetail.error }}
                </div>
                <div v-else class="loading-text">加载中...</div>
              </div>
            </div>

            <!-- 目标实体信息 -->
            <div class="detail-section" v-if="selectedRelation">
              <h5>目标实体信息</h5>
              <div class="entity-card">
                <div class="entity-header">
                  <span class="entity-type">{{ getEntityTypeText(selectedRelation.targetType) }}</span>
                  <span class="entity-id">ID: {{ selectedRelation.targetId }}</span>
                </div>
                <div class="entity-details" v-if="targetEntityDetail && !targetEntityDetail.error">
                  <!-- 票据信息 -->
                  <template v-if="selectedRelation.targetType === 'DOCUMENT'">
                    <div class="detail-row" v-if="targetEntityDetail.billNo">
                      <label>票据编号:</label>
                      <span>{{ targetEntityDetail.billNo }}</span>
                    </div>
                    <div class="detail-row" v-if="targetEntityDetail.amount">
                      <label>金额:</label>
                      <span class="amount">¥{{ formatAmount(targetEntityDetail.amount) }}</span>
                    </div>
                    <div class="detail-row" v-if="targetEntityDetail.billDate">
                      <label>票据日期:</label>
                      <span>{{ formatDate(targetEntityDetail.billDate) }}</span>
                    </div>
                    <div class="detail-row" v-if="targetEntityDetail.issuer">
                      <label>开票方:</label>
                      <span>{{ targetEntityDetail.issuer }}</span>
                    </div>
                    <div class="detail-row" v-if="targetEntityDetail.summary">
                      <label>摘要:</label>
                      <span>{{ targetEntityDetail.summary }}</span>
                    </div>
                  </template>

                  <!-- 银行回单信息 -->
                  <template v-else-if="selectedRelation.targetType === 'RECEIPT'">
                    <div class="detail-row" v-if="targetEntityDetail.receiptsNo">
                      <label>回单编号:</label>
                      <span>{{ targetEntityDetail.receiptsNo }}</span>
                    </div>
                    <div class="detail-row" v-if="targetEntityDetail.amount">
                      <label>金额:</label>
                      <span class="amount">¥{{ formatAmount(targetEntityDetail.amount) }}</span>
                    </div>
                    <div class="detail-row" v-if="targetEntityDetail.receiptsDate">
                      <label>回单日期:</label>
                      <span>{{ formatDate(targetEntityDetail.receiptsDate) }}</span>
                    </div>
                    <div class="detail-row" v-if="targetEntityDetail.payeeName">
                      <label>收款人:</label>
                      <span>{{ targetEntityDetail.payeeName }}</span>
                    </div>
                    <div class="detail-row" v-if="targetEntityDetail.payerName">
                      <label>付款人:</label>
                      <span>{{ targetEntityDetail.payerName }}</span>
                    </div>
                    <div class="detail-row" v-if="targetEntityDetail.summary">
                      <label>摘要:</label>
                      <span>{{ targetEntityDetail.summary }}</span>
                    </div>
                  </template>

                  <!-- 归并组信息 -->
                  <template v-else-if="selectedRelation.targetType === 'DOCUMENT_GROUP' || selectedRelation.targetType === 'RECEIPT_GROUP'">
                    <div class="detail-row" v-if="targetEntityDetail.groupName">
                      <label>组名称:</label>
                      <span>{{ targetEntityDetail.groupName }}</span>
                    </div>
                    <div class="detail-row" v-if="targetEntityDetail.totalAmount">
                      <label>总金额:</label>
                      <span class="amount">¥{{ formatAmount(targetEntityDetail.totalAmount) }}</span>
                    </div>
                    <div class="detail-row" v-if="targetEntityDetail.itemCount">
                      <label>包含项目:</label>
                      <span>{{ targetEntityDetail.itemCount }} 项</span>
                    </div>
                  </template>
                </div>
                <div v-else-if="targetEntityDetail && targetEntityDetail.error" class="error-text">
                  加载失败: {{ targetEntityDetail.error }}
                </div>
                <div v-else class="loading-text">加载中...</div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn secondary" @click="closeDetailModal">关闭</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { relationApi, mergeGroupApi } from '@/api/merge-relation'

export default {
  name: 'RelationManagement',
  data() {
    return {
      activeTab: 'create',

      // 标签页配置
      tabs: [
        { key: 'create', title: '创建关联', icon: 'fa fa-plus' },
        { key: 'list', title: '关联列表', icon: 'fa fa-list' },
        { key: 'batch', title: '批量关联', icon: 'fa fa-layer-group' },
        { key: 'ai', title: 'AI智能关联', icon: 'fa fa-robot' }
      ],

      // 统计数据
      totalRelations: 0,
      todayRelations: 0,
      pendingRelations: 0,

      // 选择模式
      receiptSelectionMode: 'single',
      billSelectionMode: 'single',

      // 数据
      receipts: [],
      bills: [],
      relations: [],
      receiptGroups: [],
      billGroups: [],

      // 搜索关键词
      receiptSearchKeyword: '',
      billSearchKeyword: '',
      relationSearchKeyword: '',

      // 分页相关
      receiptPagination: {
        currentPage: 1,
        pageSize: 12,
        total: 0
      },
      billPagination: {
        currentPage: 1,
        pageSize: 12,
        total: 0
      },
      relationPagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      },
      pageSizeOptions: [6, 12, 24, 48],

      // 选择的项目
      selectedReceiptId: null,
      selectedBillId: null,
      selectedReceiptGroupId: null,
      selectedBillGroupId: null,

      // 批量关联相关
      batchMode: 'auto', // auto, manual, import

      // 智能匹配设置
      autoMatchSettings: {
        byAmount: true,
        amountTolerance: 0.01,
        byDate: true,
        dateRange: 3,
        bySummary: false,
        summaryThreshold: 0.7
      },
      autoMatching: false,
      autoMatchResults: [],

      // 手动选择
      selectedReceipts: [],
      selectedBills: [],

      // 文件导入
      importResults: [],

      // AI智能关联相关
      aiConfig: {
        model: 'deepseek',
        precision: 'high',
        scope: 'all',
        autoCreate: false
      },
      // 可用AI模型列表（避免硬编码）
      availableModels: [
        { id: 'deepseek-v4-flash', name: 'DeepSeek V4 Flash', provider: 'deepseek' },
        { id: 'deepseek-v4-pro', name: 'DeepSeek V4 Pro', provider: 'deepseek' },
        { id: 'gpt-5-mini', name: 'GPT-5 Mini', provider: 'openai' }
      ],
      aiAnalyzing: false,
      aiAnalysisProgress: 0,
      aiCurrentTask: '',
      aiProcessedCount: 0,
      aiTotalCount: 0,
      aiResults: [],
      aiFeedback: {
        correct: 0,
        incorrect: 0
      },

      // 关联表单
      relationForm: {
        relationType: 'FULL',
        relationAmount: null,
        relationNote: ''
      },

      // 浮窗控制
      showImageModal: false,
      currentImageUrl: '',
      currentImageTitle: '',

      showDetailModal: false,
      selectedRelation: null,
      sourceEntityDetail: null,
      targetEntityDetail: null,

      loading: false
    }
  },

  computed: {
    // 过滤后的银行回单（不分页）
    allFilteredReceipts() {
      const sourceData = this.receiptSelectionMode === 'group' ? this.receiptGroups : this.receipts
      if (!this.receiptSearchKeyword) {
        return sourceData
      }
      const keyword = this.receiptSearchKeyword.toLowerCase()

      if (this.receiptSelectionMode === 'group') {
        return sourceData.filter(group => {
          const groupName = (group.groupName || '').toLowerCase()
          const totalAmount = (group.totalAmount || '').toString().toLowerCase()
          return groupName.includes(keyword) || totalAmount.includes(keyword)
        })
      } else {
        return sourceData.filter(receipt => {
          const receiptNo = (receipt.receiptNo || receipt.serialNumber || '').toLowerCase()
          const payerName = (receipt.payerName || '').toLowerCase()
          const payeeName = (receipt.payeeName || '').toLowerCase()
          const summary = (receipt.summary || '').toLowerCase()
          const amount = (receipt.amount || '').toString()

          return receiptNo.includes(keyword) ||
                 payerName.includes(keyword) ||
                 payeeName.includes(keyword) ||
                 summary.includes(keyword) ||
                 amount.includes(keyword)
        })
      }
    },

    // 分页后的银行回单
    filteredReceipts() {
      const filtered = this.allFilteredReceipts
      this.receiptPagination.total = filtered.length

      const start = (this.receiptPagination.currentPage - 1) * this.receiptPagination.pageSize
      const end = start + this.receiptPagination.pageSize
      return filtered.slice(start, end)
    },

    // 过滤后的票据（不分页）
    allFilteredBills() {
      const sourceData = this.billSelectionMode === 'group' ? this.billGroups : this.bills
      if (!this.billSearchKeyword) {
        return sourceData
      }
      const keyword = this.billSearchKeyword.toLowerCase()

      if (this.billSelectionMode === 'group') {
        return sourceData.filter(group => {
          const groupName = (group.groupName || '').toLowerCase()
          const totalAmount = (group.totalAmount || '').toString().toLowerCase()
          return groupName.includes(keyword) || totalAmount.includes(keyword)
        })
      } else {
        return sourceData.filter(bill => {
          const billNo = (bill.billNo || bill.invoiceNumber || '').toLowerCase()
          const issuer = (bill.issuer || '').toLowerCase()
          const recipient = (bill.recipient || '').toLowerCase()
          const summary = (bill.summary || '').toLowerCase()
          const amount = (bill.amount || '').toString()

          return billNo.includes(keyword) ||
                 issuer.includes(keyword) ||
                 recipient.includes(keyword) ||
                 summary.includes(keyword) ||
                 amount.includes(keyword)
        })
      }
    },

    // 分页后的票据
    filteredBills() {
      const filtered = this.allFilteredBills
      this.billPagination.total = filtered.length

      const start = (this.billPagination.currentPage - 1) * this.billPagination.pageSize
      const end = start + this.billPagination.pageSize
      return filtered.slice(start, end)
    },

    // 过滤后的关联关系（不分页）
    allFilteredRelations() {
      if (!this.relationSearchKeyword) {
        return this.relations
      }
      const keyword = this.relationSearchKeyword.toLowerCase()
      return this.relations.filter(relation => {
        const sourceId = (relation.sourceId || '').toLowerCase()
        const targetId = (relation.targetId || '').toLowerCase()
        const note = (relation.relationNote || '').toLowerCase()

        return sourceId.includes(keyword) ||
               targetId.includes(keyword) ||
               note.includes(keyword)
      })
    },

    // 分页后的关联关系
    filteredRelations() {
      const filtered = this.allFilteredRelations
      this.relationPagination.total = filtered.length

      const start = (this.relationPagination.currentPage - 1) * this.relationPagination.pageSize
      const end = start + this.relationPagination.pageSize
      return filtered.slice(start, end)
    },

    canCreateRelation() {
      const hasReceiptSelection = this.receiptSelectionMode === 'group'
        ? this.selectedReceiptGroupId
        : this.selectedReceiptId
      const hasBillSelection = this.billSelectionMode === 'group'
        ? this.selectedBillGroupId
        : this.selectedBillId
      return hasReceiptSelection && hasBillSelection
    },

    // 批量关联相关计算属性
    hasSelectedMatches() {
      return this.autoMatchResults.some(result => result.selected)
    },

    selectedMatchesCount() {
      return this.autoMatchResults.filter(result => result.selected).length
    },

    canCreateManualRelations() {
      return this.selectedReceipts.length > 0 && this.selectedBills.length > 0
    },

    // AI智能关联相关计算属性
    unanalyzedReceipts() {
      return this.receipts.length
    },

    unanalyzedBills() {
      return this.bills.length
    },

    highConfidenceResults() {
      return this.aiResults.filter(result => result.confidence >= 0.9)
    },

    mediumConfidenceResults() {
      return this.aiResults.filter(result => result.confidence >= 0.7 && result.confidence < 0.9)
    },

    lowConfidenceResults() {
      return this.aiResults.filter(result => result.confidence < 0.7)
    },

    hasSelectedAIResults() {
      return this.aiResults.some(result => result.selected)
    },

    selectedAIResultsCount() {
      return this.aiResults.filter(result => result.selected).length
    },

    aiAccuracyRate() {
      const total = this.aiFeedback.correct + this.aiFeedback.incorrect
      if (total === 0) return 0
      return ((this.aiFeedback.correct / total) * 100).toFixed(1)
    }
  },

  created() {
    this.loadData()
  },

  methods: {
    // 判断某张回单是否已有关联（直接或通过所属组）
    isReceiptAssociated(receiptId) {
      if (!receiptId || !Array.isArray(this.relations)) return false
      // 直接关联到该回单
      const direct = this.relations.some(rel =>
        (rel.sourceType === 'RECEIPT' && String(rel.sourceId) === String(receiptId)) ||
        (rel.targetType === 'RECEIPT' && String(rel.targetId) === String(receiptId))
      )
      if (direct) return true
      // 若该回单属于某个回单组，且该组被建立了关联，也视为已关联
      if (Array.isArray(this.receiptGroups) && this.receiptGroups.length) {
        const belongGroups = this.receiptGroups.filter(g => Array.isArray(g.items) && g.items.some(it => String(it.id) === String(receiptId)))
        const groupIds = belongGroups.map(g => String(g.groupId))
        if (groupIds.length) {
          return this.relations.some(rel =>
            (rel.sourceType === 'RECEIPT_GROUP' && groupIds.includes(String(rel.sourceId))) ||
            (rel.targetType === 'RECEIPT_GROUP' && groupIds.includes(String(rel.targetId)))
          )
        }
      }
      return false
    },

    // 判断某张票据是否已有关联（直接或通过所属组）
    isBillAssociated(billId) {
      if (!billId || !Array.isArray(this.relations)) return false
      // 直接关联到该票据
      const direct = this.relations.some(rel =>
        (rel.sourceType === 'DOCUMENT' && String(rel.sourceId) === String(billId)) ||
        (rel.targetType === 'DOCUMENT' && String(rel.targetId) === String(billId))
      )
      if (direct) return true
      // 若该票据属于某个票据组，且该组被建立了关联，也视为已关联
      if (Array.isArray(this.billGroups) && this.billGroups.length) {
        const belongGroups = this.billGroups.filter(g => Array.isArray(g.items) && g.items.some(it => String(it.id) === String(billId)))
        const groupIds = belongGroups.map(g => String(g.groupId))
        if (groupIds.length) {
          return this.relations.some(rel =>
            (rel.sourceType === 'DOCUMENT_GROUP' && groupIds.includes(String(rel.sourceId))) ||
            (rel.targetType === 'DOCUMENT_GROUP' && groupIds.includes(String(rel.targetId)))
          )
        }
      }
      return false
    },

    // 判断回单组是否有关联（任一成员被关联即视为已关联）
    isReceiptGroupAssociated(groupId) {
      if (!groupId || !Array.isArray(this.receiptGroups) || !Array.isArray(this.relations)) return false
      // 1) 直接与组建立的关联
      const directGroup = this.relations.some(rel =>
        (rel.sourceType === 'RECEIPT_GROUP' && String(rel.sourceId) === String(groupId)) ||
        (rel.targetType === 'RECEIPT_GROUP' && String(rel.targetId) === String(groupId))
      )
      if (directGroup) return true
      // 2) 组内任一成员被关联
      const group = this.receiptGroups.find(g => String(g.groupId) === String(groupId))
      if (!group || !Array.isArray(group.items)) return false
      const memberIds = group.items.map(it => String(it.id))
      return this.relations.some(rel =>
        (rel.sourceType === 'RECEIPT' && memberIds.includes(String(rel.sourceId))) ||
        (rel.targetType === 'RECEIPT' && memberIds.includes(String(rel.targetId)))
      )
    },

    // 判断票据组是否有关联（任一成员被关联即视为已关联）
    isBillGroupAssociated(groupId) {
      if (!groupId || !Array.isArray(this.billGroups) || !Array.isArray(this.relations)) return false
      // 1) 直接与组建立的关联
      const directGroup = this.relations.some(rel =>
        (rel.sourceType === 'DOCUMENT_GROUP' && String(rel.sourceId) === String(groupId)) ||
        (rel.targetType === 'DOCUMENT_GROUP' && String(rel.targetId) === String(groupId))
      )
      if (directGroup) return true
      // 2) 组内任一成员被关联
      const group = this.billGroups.find(g => String(g.groupId) === String(groupId))
      if (!group || !Array.isArray(group.items)) return false
      const memberIds = group.items.map(it => String(it.id))
      return this.relations.some(rel =>
        (rel.sourceType === 'DOCUMENT' && memberIds.includes(String(rel.sourceId))) ||
        (rel.targetType === 'DOCUMENT' && memberIds.includes(String(rel.targetId)))
      )
    },
    async loadData() {
      this.loading = true
      try {
        await Promise.all([
          this.loadReceipts(),
          this.loadBills(),
          this.loadRelations(),
          this.loadStatistics(),
          this.loadReceiptGroups(),
          this.loadBillGroups()
        ])
      } catch (error) {
        console.error('加载数据失败:', error)
        this.$Message.error('加载数据失败')
      } finally {
        this.loading = false
      }
    },

    async loadReceipts() {
      try {
        const response = await fetch('/api/merge-engine/unmerged-receipts', {
          credentials: 'include'
        })
        const result = await response.json()
        if (result.success) {
          this.receipts = result.data || []
        }
      } catch (error) {
        console.error('加载银行回单失败:', error)
      }
    },

    async loadBills() {
      try {
        const response = await fetch('/api/merge-engine/unmerged-documents', {
          credentials: 'include'
        })
        const result = await response.json()
        if (result.success) {
          this.bills = result.data || []
        }
      } catch (error) {
        console.error('加载票据失败:', error)
      }
    },

    async loadRelations() {
      try {
        const response = await fetch('/api/entity-relations/list?page=1&size=1000', {
          credentials: 'include'
        })
        const result = await response.json()
        if (result.success) {
          this.relations = (result.data && result.data.records) || []
          console.log('加载关联关系成功:', this.relations.length)
        } else {
          console.error('加载关联关系失败:', result.msg)
        }
      } catch (error) {
        console.error('加载关联关系失败:', error)
      }
    },

    async loadStatistics() {
      // 计算统计数据
      this.totalRelations = this.relations.length

      // 计算今日新增
      const today = new Date().toDateString()
      this.todayRelations = this.relations.filter(relation => {
        const createdDate = new Date(relation.createdAt).toDateString()
        return createdDate === today
      }).length

      // 待处理数量（这里可以根据业务逻辑调整）
      this.pendingRelations = 0
    },

    async loadReceiptGroups() {
      try {
        const response = await fetch('/api/merge-groups/receipts', {
          credentials: 'include'
        })
        const result = await response.json()
        if (result.success) {
          this.receiptGroups = result.data || []
          console.log('加载银行回单归并组成功:', this.receiptGroups.length)
        } else {
          console.error('加载银行回单归并组失败:', result.message)
        }
      } catch (error) {
        console.error('加载银行回单归并组失败:', error)
      }
    },

    async loadBillGroups() {
      try {
        const response = await fetch('/api/merge-groups/documents', {
          credentials: 'include'
        })
        const result = await response.json()
        if (result.success) {
          this.billGroups = result.data || []
          console.log('加载票据归并组成功:', this.billGroups.length)
        } else {
          console.error('加载票据归并组失败:', result.message)
        }
      } catch (error) {
        console.error('加载票据归并组失败:', error)
      }
    },

    // 搜索方法
    searchReceipts() {
      // 搜索时重置到第一页
      this.receiptPagination.currentPage = 1
    },

    searchBills() {
      // 搜索时重置到第一页
      this.billPagination.currentPage = 1
    },

    searchRelations() {
      // 搜索时重置到第一页
      this.relationPagination.currentPage = 1
    },

    // 分页方法
    handleReceiptPageChange(page) {
      this.receiptPagination.currentPage = page
    },

    handleReceiptPageSizeChange(pageSize) {
      this.receiptPagination.pageSize = pageSize
      this.receiptPagination.currentPage = 1
    },

    handleBillPageChange(page) {
      this.billPagination.currentPage = page
    },

    handleBillPageSizeChange(pageSize) {
      this.billPagination.pageSize = pageSize
      this.billPagination.currentPage = 1
    },

    handleRelationPageChange(page) {
      this.relationPagination.currentPage = page
    },

    handleRelationPageSizeChange(pageSize) {
      this.relationPagination.pageSize = pageSize
      this.relationPagination.currentPage = 1
    },

    // 获取页码数组
    getPageNumbers(pagination) {
      const totalPages = Math.ceil(pagination.total / pagination.pageSize)
      const currentPage = pagination.currentPage
      const pages = []

      if (totalPages <= 7) {
        // 总页数小于等于7，显示所有页码
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i)
        }
      } else {
        // 总页数大于7，显示省略号
        if (currentPage <= 4) {
          // 当前页在前面
          for (let i = 1; i <= 5; i++) {
            pages.push(i)
          }
          pages.push('...')
          pages.push(totalPages)
        } else if (currentPage >= totalPages - 3) {
          // 当前页在后面
          pages.push(1)
          pages.push('...')
          for (let i = totalPages - 4; i <= totalPages; i++) {
            pages.push(i)
          }
        } else {
          // 当前页在中间
          pages.push(1)
          pages.push('...')
          for (let i = currentPage - 1; i <= currentPage + 1; i++) {
            pages.push(i)
          }
          pages.push('...')
          pages.push(totalPages)
        }
      }

      return pages
    },

    // 智能推荐
    async loadRecommendedReceipts() {
      this.$Message.info('智能推荐功能开发中...')
    },

    async loadRecommendedBills() {
      this.$Message.info('智能推荐功能开发中...')
    },

    // 选择方法
    selectReceipt(itemId) {
      if (this.receiptSelectionMode === 'group') {
        this.selectedReceiptGroupId = itemId
        this.selectedReceiptId = null
      } else {
        this.selectedReceiptId = itemId
        this.selectedReceiptGroupId = null
      }
    },

    // 交互拦截：点击已关联的卡片仅提示、不选择
    handleClickReceipt(receipt) {
      if (this.isReceiptAssociated(receipt.id)) {
        this.$Message.warning('该回单已有关联，若需变更请先解除关联')
        return
      }
      this.selectReceipt(receipt.id)
    },

    handleClickReceiptGroup(group) {
      if (this.isReceiptGroupAssociated(group.groupId)) {
        this.$Message.warning('该回单组合中存在已关联项，请先解除或更换再关联')
        return
      }
      this.selectReceipt(group.groupId)
    },

    handleClickBill(bill) {
      if (this.isBillAssociated(bill.id)) {
        this.$Message.warning('该票据已有关联，若需变更请先解除关联')
        return
      }
      this.selectBill(bill.id)
    },

    handleClickBillGroup(group) {
      if (this.isBillGroupAssociated(group.groupId)) {
        this.$Message.warning('该票据组合中存在已关联项，请先解除或更换再关联')
        return
      }
      this.selectBill(group.groupId)
    },


    selectBill(itemId) {
      if (this.billSelectionMode === 'group') {
        this.selectedBillGroupId = itemId
        this.selectedBillId = null
      } else {
        this.selectedBillId = itemId
        this.selectedBillGroupId = null
      }
    },

    // 创建关联
    async createRelation() {
      if (!this.canCreateRelation) {
        this.$Message.warning('请先选择银行回单和票据')
        return
      }

      try {
        const createDto = {
          sourceType: this.receiptSelectionMode === 'group' ? 'RECEIPT_GROUP' : 'RECEIPT',
          sourceId: this.receiptSelectionMode === 'group' ? this.selectedReceiptGroupId : this.selectedReceiptId,
          targetType: this.billSelectionMode === 'group' ? 'DOCUMENT_GROUP' : 'DOCUMENT',
          targetId: this.billSelectionMode === 'group' ? this.selectedBillGroupId : this.selectedBillId,
          relationType: this.relationForm.relationType,
          relationAmount: this.relationForm.relationAmount,
          relationNote: this.relationForm.relationNote
        }

        const result = await relationApi.createRelation(createDto)
        if (result.success) {
          this.$Message.success('关联创建成功')
          this.resetForm()
          await this.loadRelations()
          await this.loadStatistics()
        } else {
          this.$Message.error(result.message || '关联创建失败')
        }
      } catch (error) {
        console.error('创建关联失败:', error)
        this.$Message.error('创建关联失败: ' + error.message)
      }
    },

    // 重置表单
    resetForm() {
      this.selectedReceiptId = null
      this.selectedBillId = null
      this.selectedReceiptGroupId = null
      this.selectedBillGroupId = null
      this.relationForm = {
        relationType: 'FULL',
        relationAmount: null,
        relationNote: ''
      }
      this.receiptSearchKeyword = ''
      this.billSearchKeyword = ''
    },

    // 删除关联
    async deleteRelation(relationId) {
      if (!confirm('确定要删除这个关联关系吗？')) {
        return
      }

      try {
        const result = await relationApi.deleteRelation(relationId)
        console.log('删除关联API响应:', result)

        if (result && result.success) {
          this.$Message.success('关联删除成功')
          await this.loadRelations()
          await this.loadStatistics()
        } else {
          const errorMsg = (result && result.message) || (result && result.msg) || '关联删除失败'
          this.$Message.error(errorMsg)
        }
      } catch (error) {
        console.error('删除关联失败:', error)

        // 改进错误处理，避免访问undefined的属性
        let errorMessage = '删除关联失败'
        if (error && error.response && error.response.data) {
          errorMessage += ': ' + (error.response.data.message || error.response.data.msg || '服务器错误')
        } else if (error && error.message) {
          errorMessage += ': ' + error.message
        } else if (typeof error === 'string') {
          errorMessage += ': ' + error
        }

        this.$Message.error(errorMessage)
      }
    },

    // 显示关联详情
    async showRelationDetail(relation) {
      console.log('显示关联详情:', relation)
      this.selectedRelation = relation
      this.sourceEntityDetail = null
      this.targetEntityDetail = null
      this.showDetailModal = true

      // 异步加载实体详情
      await this.loadEntityDetails()
    },

    // 关闭详情浮窗
    closeDetailModal() {
      this.showDetailModal = false
      this.selectedRelation = null
      this.sourceEntityDetail = null
      this.targetEntityDetail = null
    },

    // 加载实体详情信息
    async loadEntityDetails() {
      if (!this.selectedRelation) return

      try {
        console.log('开始加载实体详情...')

        // 加载源实体详情
        console.log('加载源实体:', this.selectedRelation.sourceType, this.selectedRelation.sourceId)
        this.sourceEntityDetail = await this.loadEntityDetail(
          this.selectedRelation.sourceType,
          this.selectedRelation.sourceId
        )
        console.log('源实体详情:', this.sourceEntityDetail)

        // 加载目标实体详情
        console.log('加载目标实体:', this.selectedRelation.targetType, this.selectedRelation.targetId)
        this.targetEntityDetail = await this.loadEntityDetail(
          this.selectedRelation.targetType,
          this.selectedRelation.targetId
        )
        console.log('目标实体详情:', this.targetEntityDetail)
      } catch (error) {
        console.error('加载实体详情失败:', error)
      }
    },

    // 加载单个实体详情
    async loadEntityDetail(entityType, entityId) {
      try {
        let apiUrl = ''

        if (entityType === 'DOCUMENT') {
          // 加载票据详情
          apiUrl = `/api/bill/${entityId}`
        } else if (entityType === 'RECEIPT') {
          // 加载银证详情
          apiUrl = `/api/bank-receipts/${entityId}`
        } else if (entityType === 'DOCUMENT_GROUP') {
          // 加载票据组详情
          apiUrl = `/api/merge-groups/documents/${entityId}`
        } else if (entityType === 'RECEIPT_GROUP') {
          // 加载银证组详情
          apiUrl = `/api/merge-groups/receipts/${entityId}`
        } else {
          console.error('未知实体类型:', entityType)
          return { error: '未知实体类型: ' + entityType }
        }

        console.log('请求API:', apiUrl)
        const response = await fetch(apiUrl, {
          credentials: 'include'
        })

        console.log('API响应状态:', response.status)
        if (!response.ok) {
          return { error: `HTTP ${response.status}: ${response.statusText}` }
        }

        const result = await response.json()
        console.log('API响应结果:', result)

        if (result.success) {
          return result.data
        } else {
          return { error: result.msg || result.message || '加载失败' }
        }
      } catch (error) {
        console.error('加载实体详情失败:', error)
        return { error: error.message }
      }
    },

    // 图片预览
    showImagePreview(imageUrl, title) {
      this.currentImageUrl = imageUrl
      this.currentImageTitle = title
      this.showImageModal = true
    },

    closeImageModal() {
      this.showImageModal = false
      this.currentImageUrl = ''
      this.currentImageTitle = ''
    },

    // 工具方法
    formatAmount(amount) {
      if (!amount) return '0.00'
      return Number(amount).toLocaleString('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },

    formatDate(date) {
      if (!date) return '无'
      return new Date(date).toLocaleDateString('zh-CN')
    },

    getRelationTypeText(type) {
      const typeMap = {
        'FULL': '完全关联',
        'PARTIAL': '部分关联',
        'AI_GENERATED': 'AI生成'
      }
      return typeMap[type] || type
    },

    getEntityTypeText(type) {
      const typeMap = {
        'RECEIPT': '银行回单',
        'DOCUMENT': '票据',
        'RECEIPT_GROUP': '回单组',
        'DOCUMENT_GROUP': '票据组',
        'VOUCHER': '凭证'
      }
      return typeMap[type] || type
    },

    // 批量关联方法
    async startAutoMatch() {
      this.autoMatching = true
      this.autoMatchResults = []

      try {
        // 模拟智能匹配过程
        const receipts = this.allFilteredReceipts
        const bills = this.allFilteredBills

        for (const receipt of receipts) {
          for (const bill of bills) {
            const matchResult = this.calculateMatch(receipt, bill)
            if (matchResult.confidence > 0.5) {
              this.autoMatchResults.push({
                receipt,
                bill,
                confidence: matchResult.confidence,
                reasons: matchResult.reasons,
                selected: matchResult.confidence > 0.8
              })
            }
          }
        }

        // 按置信度排序
        this.autoMatchResults.sort((a, b) => b.confidence - a.confidence)

        this.$Message.success(`智能匹配完成，发现 ${this.autoMatchResults.length} 个潜在关联`)
      } catch (error) {
        console.error('智能匹配失败:', error)
        this.$Message.error('智能匹配失败: ' + error.message)
      } finally {
        this.autoMatching = false
      }
    },

    calculateMatch(receipt, bill) {
      let confidence = 0
      const reasons = []

      // 金额匹配
      if (this.autoMatchSettings.byAmount) {
        const amountDiff = Math.abs(receipt.amount - bill.amount)
        if (amountDiff <= this.autoMatchSettings.amountTolerance) {
          confidence += 0.4
          reasons.push('金额完全匹配')
        } else if (amountDiff <= receipt.amount * 0.1) {
          confidence += 0.2
          reasons.push('金额近似匹配')
        }
      }

      // 日期匹配
      if (this.autoMatchSettings.byDate) {
        const receiptDate = new Date(receipt.receiptsDate || receipt.transferDate)
        const billDate = new Date(bill.billDate)
        const daysDiff = Math.abs((receiptDate - billDate) / (1000 * 60 * 60 * 24))

        if (daysDiff <= this.autoMatchSettings.dateRange) {
          confidence += 0.3
          reasons.push(`日期匹配(相差${Math.floor(daysDiff)}天)`)
        }
      }

      // 摘要匹配
      if (this.autoMatchSettings.bySummary) {
        const receiptSummary = (receipt.summary || '').toLowerCase()
        const billSummary = (bill.summary || '').toLowerCase()
        const similarity = this.calculateStringSimilarity(receiptSummary, billSummary)

        if (similarity >= this.autoMatchSettings.summaryThreshold) {
          confidence += 0.3
          reasons.push(`摘要相似度${(similarity * 100).toFixed(1)}%`)
        }
      }

      return { confidence: Math.min(confidence, 1), reasons }
    },

    calculateStringSimilarity(str1, str2) {
      if (!str1 || !str2) return 0

      const longer = str1.length > str2.length ? str1 : str2
      const shorter = str1.length > str2.length ? str2 : str1

      if (longer.length === 0) return 1

      const editDistance = this.levenshteinDistance(longer, shorter)
      return (longer.length - editDistance) / longer.length
    },

    levenshteinDistance(str1, str2) {
      const matrix = []

      for (let i = 0; i <= str2.length; i++) {
        matrix[i] = [i]
      }

      for (let j = 0; j <= str1.length; j++) {
        matrix[0][j] = j
      }

      for (let i = 1; i <= str2.length; i++) {
        for (let j = 1; j <= str1.length; j++) {
          if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
            matrix[i][j] = matrix[i - 1][j - 1]
          } else {
            matrix[i][j] = Math.min(
              matrix[i - 1][j - 1] + 1,
              matrix[i][j - 1] + 1,
              matrix[i - 1][j] + 1
            )
          }
        }
      }

      return matrix[str2.length][str1.length]
    },

    clearAutoResults() {
      this.autoMatchResults = []
    },

    viewMatchDetail(result) {
      // 显示匹配详情
      this.$Message.info('查看匹配详情功能开发中...')
    },

    removeMatchResult(index) {
      this.autoMatchResults.splice(index, 1)
    },

    selectAllMatches() {
      this.autoMatchResults.forEach(result => {
        result.selected = true
      })
    },

    deselectAllMatches() {
      this.autoMatchResults.forEach(result => {
        result.selected = false
      })
    },

    async createBatchRelations() {
      const selectedMatches = this.autoMatchResults.filter(result => result.selected)

      if (selectedMatches.length === 0) {
        this.$Message.warning('请先选择要创建的关联')
        return
      }

      try {
        let successCount = 0
        let errorCount = 0

        for (const match of selectedMatches) {
          const createDto = {
            sourceType: 'RECEIPT',
            sourceId: match.receipt.receiptNo || match.receipt.serialNumber,
            targetType: 'DOCUMENT',
            targetId: match.bill.billNo || match.bill.invoiceNumber,
            relationType: 'FULL',
            relationNote: `智能匹配创建 - 置信度: ${(match.confidence * 100).toFixed(1)}%`
          }

          try {
            const result = await relationApi.createRelation(createDto)
            if (result.success) {
              successCount++
            } else {
              errorCount++
            }
          } catch (error) {
            errorCount++
          }
        }

        this.$Message.success(`批量关联完成: 成功 ${successCount} 个，失败 ${errorCount} 个`)

        // 清空已创建的匹配结果
        this.autoMatchResults = this.autoMatchResults.filter(result => !result.selected)

        // 刷新关联列表
        await this.loadRelations()
        await this.loadStatistics()

      } catch (error) {
        console.error('批量创建关联失败:', error)
        this.$Message.error('批量创建关联失败: ' + error.message)
      }
    },

    // 手动选择模式方法
    clearManualSelection() {
      this.selectedReceipts = []
      this.selectedBills = []
    },

    async createManualRelations() {
      this.$Message.info('手动批量关联功能开发中...')
    },

    // 文件导入方法
    downloadTemplate() {
      // 创建Excel模板
      const templateData = [
        ['银行回单编号', '票据编号', '关联类型', '关联金额', '备注说明'],
        ['C02471R0004Z69Z', 'PJ202507-0001', 'FULL', '', '示例数据'],
        ['C02471R0003WCSZ', 'PJ202507-0001005', 'PARTIAL', '500.00', '示例数据']
      ]

      // 这里应该使用Excel导出库，暂时用简单提示
      this.$Message.info('模板下载功能开发中...')
    },

    selectImportFile() {
      this.$refs.fileInput.click()
    },

    handleFileImport(event) {
      const file = event.target.files[0]
      if (!file) return

      this.$Message.info('文件导入功能开发中...')
      this.importResults = [
        { success: true, message: '第1行数据导入成功' },
        { success: false, message: '第2行数据格式错误' },
        { success: true, message: '第3行数据导入成功' }
      ]
    },

    // AI智能关联方法
    async startAIAnalysis() {
      this.aiAnalyzing = true
      this.aiAnalysisProgress = 0
      this.aiCurrentTask = '初始化AI分析...'
      this.aiProcessedCount = 0
      this.aiTotalCount = this.receipts.length + this.bills.length
      this.aiResults = []

      try {
        // 模拟AI分析过程
        this.aiCurrentTask = '分析银行回单...'
        await this.simulateAIProgress(30)

        this.aiCurrentTask = '分析票据信息...'
        await this.simulateAIProgress(60)

        this.aiCurrentTask = '生成关联建议...'
        await this.simulateAIProgress(90)

        // 生成模拟AI结果
        await this.generateAIResults()

        this.aiCurrentTask = 'AI分析完成'
        this.aiAnalysisProgress = 100

        this.$Message.success(`AI分析完成，发现 ${this.aiResults.length} 个潜在关联`)

      } catch (error) {
        console.error('AI分析失败:', error)
        this.$Message.error('AI分析失败: ' + error.message)
      } finally {
        setTimeout(() => {
          this.aiAnalyzing = false
        }, 1000)
      }
    },

    async simulateAIProgress(targetProgress) {
      return new Promise(resolve => {
        const interval = setInterval(() => {
          this.aiAnalysisProgress += 2
          this.aiProcessedCount = Math.floor((this.aiAnalysisProgress / 100) * this.aiTotalCount)

          if (this.aiAnalysisProgress >= targetProgress) {
            clearInterval(interval)
            resolve()
          }
        }, 100)
      })
    },

    async generateAIResults() {
      const receipts = this.allFilteredReceipts.slice(0, 5) // 取前5个
      const bills = this.allFilteredBills.slice(0, 5) // 取前5个

      for (let i = 0; i < Math.min(receipts.length, bills.length); i++) {
        const receipt = receipts[i]
        const bill = bills[i]

        const confidence = 0.6 + Math.random() * 0.4 // 0.6-1.0之间的随机置信度

        this.aiResults.push({
          receipt,
          bill,
          confidence,
          analysisReasons: [
            '金额匹配度高',
            '时间窗口内发生',
            '交易对手信息一致',
            '业务类型匹配'
          ],
          suggestion: confidence > 0.9 ? '强烈建议建立关联' : confidence > 0.7 ? '建议建立关联' : '可考虑建立关联',
          selected: confidence > 0.8
        })
      }
    },

    pauseAIAnalysis() {
      this.$Message.info('暂停AI分析功能开发中...')
    },

    stopAIAnalysis() {
      this.aiAnalyzing = false
      this.aiAnalysisProgress = 0
      this.aiCurrentTask = ''
      this.$Message.info('AI分析已停止')
    },

    selectHighConfidence() {
      this.aiResults.forEach(result => {
        result.selected = result.confidence >= 0.9
      })
    },

    selectAllAIResults() {
      this.aiResults.forEach(result => {
        result.selected = true
      })
    },

    async createSelectedAIRelations() {
      const selectedResults = this.aiResults.filter(result => result.selected)

      if (selectedResults.length === 0) {
        this.$Message.warning('请先选择要创建的关联')
        return
      }

      try {
        let successCount = 0
        let errorCount = 0

        for (const result of selectedResults) {
          const createDto = {
            sourceType: 'RECEIPT',
            sourceId: result.receipt.receiptNo || result.receipt.serialNumber,
            targetType: 'DOCUMENT',
            targetId: result.bill.billNo || result.bill.invoiceNumber,
            relationType: 'FULL',
            relationNote: `AI智能关联 - 置信度: ${(result.confidence * 100).toFixed(1)}%`
          }

          try {
            const apiResult = await relationApi.createRelation(createDto)
            if (apiResult.success) {
              successCount++
              // 记录正确反馈
              this.aiFeedback.correct++
            } else {
              errorCount++
            }
          } catch (error) {
            errorCount++
          }
        }

        this.$Message.success(`AI关联完成: 成功 ${successCount} 个，失败 ${errorCount} 个`)

        // 清空已创建的结果
        this.aiResults = this.aiResults.filter(result => !result.selected)

        // 刷新关联列表
        await this.loadRelations()
        await this.loadStatistics()

      } catch (error) {
        console.error('创建AI关联失败:', error)
        this.$Message.error('创建AI关联失败: ' + error.message)
      }
    },

    viewAIResultDetail(result) {
      this.$Message.info('查看AI分析详情功能开发中...')
    },

    removeAIResult(index) {
      this.aiResults.splice(index, 1)
    },

    getConfidenceClass(confidence) {
      if (confidence >= 0.9) return 'high'
      if (confidence >= 0.7) return 'medium'
      return 'low'
    }
  }
}
</script>

<style scoped>
.relation-management {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

/* 页面头部 */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  margin-bottom: 20px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 15px;
}

.header-info h1 {
  margin: 0 0 5px 0;
  font-size: 24px;
  color: #2c3e50;
}

.header-desc {
  color: #7f8c8d;
  font-size: 14px;
}

.header-stats {
  display: flex;
  gap: 20px;
}

.stat-item {
  padding: 8px 16px;
  background: #ecf0f1;
  border-radius: 20px;
  font-size: 14px;
  color: #2c3e50;
}

/* 标签页导航 */
.tab-navigation {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  margin-bottom: 20px;
  overflow: hidden;
}

.tab-container {
  display: flex;
}

.tab-button {
  flex: 1;
  padding: 16px 20px;
  border: none;
  background: white;
  color: #7f8c8d;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  border-bottom: 3px solid transparent;
}

.tab-button:hover {
  background: #f8f9fa;
  color: #2c3e50;
}

.tab-button.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-bottom-color: #764ba2;
}

.tab-button i {
  font-size: 16px;
}

/* 内容区域 */
.content-area {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  overflow: hidden;
}

.tab-content {
  padding: 30px;
}

/* 创建关联 */
.create-relation-container {
  max-width: 1200px;
  margin: 0 auto;
}

.form-header {
  text-align: center;
  margin-bottom: 40px;
}

.form-header h2 {
  margin: 0 0 10px 0;
  color: #2c3e50;
  font-size: 28px;
}

.form-header p {
  color: #7f8c8d;
  font-size: 16px;
  margin: 0;
}

.relation-form {
  display: flex;
  flex-direction: column;
  gap: 40px;
}

/* 选择区域 */
.selection-section {
  background: #fafbfc;
  border-radius: 12px;
  padding: 25px;
  border: 1px solid #e8ecef;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.section-header h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 18px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.section-header i {
  color: #667eea;
}

.selection-mode {
  display: flex;
  gap: 20px;
}

.radio-option {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #2c3e50;
}

.radio-option input[type="radio"] {
  margin: 0;
}

/* 搜索控件 */
.search-controls {
  display: flex;
  gap: 15px;
  margin-bottom: 25px;
  align-items: center;
}

.search-input-group {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
}

.search-input-group i {
  position: absolute;
  left: 12px;
  color: #7f8c8d;
  z-index: 1;
}

.search-input {
  width: 100%;
  padding: 12px 12px 12px 40px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.3s;
}

.search-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

/* 项目网格 */
.items-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
}

.item-card {
  border: 2px solid #e8ecef;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s;
  cursor: pointer;
  background: white;
}


/* 已关联标记样式 */
.item-card.associated {
  border-color: #52c41a;
  position: relative;
}

.card-flags {
  display: flex;
  align-items: center;
  gap: 8px;
}

.associated-flag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #f6ffed;
  color: #389e0d;
  border: 1px solid #b7eb8f;
  font-size: 12px;
}

.item-card.associated::after {
  content: '已关联';
  position: absolute;
  top: 8px;
  left: -36px;
  transform: rotate(-45deg);
  background: #52c41a;
  color: #fff;
  padding: 2px 40px;
  font-size: 12px;
}

.item-card:hover {
  box-shadow: 0 8px 25px rgba(0,0,0,0.1);
  transform: translateY(-2px);
}

.item-card.selected {
  border-color: #667eea;
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.2);
}

.card-header {
  background: #f8f9fa;
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.item-title {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.item-number {
  font-weight: bold;
  color: #2c3e50;
  font-size: 14px;
}

.item-amount {
  font-weight: bold;
  color: #27ae60;
  font-size: 16px;
}

.selection-indicator {
  opacity: 0;
  transition: opacity 0.3s;
}

.item-card.selected .selection-indicator {
  opacity: 1;
}

.selection-indicator i {
  color: #667eea;
  font-size: 20px;
}

.card-content {
  padding: 20px;
  display: flex;
  gap: 15px;
}

.item-info {
  flex: 1;
}

.info-row {
  display: flex;
  margin-bottom: 8px;
  font-size: 13px;
}

.info-row label {
  width: 60px;
  font-weight: bold;
  color: #7f8c8d;
}

.info-row span {
  color: #2c3e50;
}

.item-thumbnail {
  width: 80px;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
}

.item-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s;
}

.item-thumbnail img:hover {
  transform: scale(1.1);
}

/* 关联设置 */
.relation-settings {
  background: #fafbfc;
  border-radius: 12px;
  padding: 25px;
  border: 1px solid #e8ecef;
}

.settings-header h3 {
  margin: 0 0 20px 0;
  color: #2c3e50;
  font-size: 18px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.settings-header i {
  color: #667eea;
}

.settings-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.form-group label {
  font-weight: bold;
  color: #2c3e50;
  font-size: 14px;
}

.radio-group {
  display: flex;
  gap: 20px;
}

.form-input, .form-textarea {
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.3s;
}

.form-input:focus, .form-textarea:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-textarea {
  resize: vertical;
  min-height: 80px;
}

.form-actions {
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 10px;
}

/* 按钮样式 */
.btn {
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn.primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn.primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn.secondary {
  background: #6c757d;
  color: white;
}

.btn.secondary:hover {
  background: #5a6268;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(108, 117, 125, 0.3);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 关联列表 */
.relation-list-container {
  max-width: 1200px;
  margin: 0 auto;
}

.list-header {
  text-align: center;
  margin-bottom: 30px;
}

.list-header h2 {
  margin: 0 0 10px 0;
  color: #2c3e50;
  font-size: 28px;
}

.list-header p {
  color: #7f8c8d;
  font-size: 16px;
  margin: 0;
}

.list-controls {
  display: flex;
  gap: 15px;
  margin-bottom: 25px;
  align-items: center;
}

.relations-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 20px;
}

.relation-card {
  border: 1px solid #e8ecef;
  border-radius: 12px;
  overflow: hidden;
  background: white;
  transition: all 0.3s;
}

.relation-card:hover {
  box-shadow: 0 8px 25px rgba(0,0,0,0.1);
  transform: translateY(-2px);
}

.relation-header {
  background: #f8f9fa;
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.relation-type {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: bold;
  color: #2c3e50;
}

.relation-type i {
  color: #667eea;
}

.relation-actions {
  display: flex;
  gap: 10px;
}

.action-btn {
  padding: 6px 10px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  background: #ecf0f1;
  color: #2c3e50;
  transition: all 0.3s;
}

.action-btn:hover {
  background: #d5dbdb;
}

.action-btn.danger {
  background: #e74c3c;
  color: white;
}

.action-btn.danger:hover {
  background: #c0392b;
}

.relation-content {
  padding: 20px;
}

.relation-entities {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
}

.entity {
  text-align: center;
  flex: 1;
}

.entity-type {
  font-size: 12px;
  color: #7f8c8d;
  margin-bottom: 5px;
}

.entity-id {
  font-weight: bold;
  color: #2c3e50;
  font-size: 14px;
}

.relation-arrow {
  margin: 0 20px;
  color: #667eea;
  font-size: 18px;
}

.relation-info .info-row {
  display: flex;
  margin-bottom: 8px;
  font-size: 13px;
}

.relation-info .info-row label {
  width: 80px;
  font-weight: bold;
  color: #7f8c8d;
}

.relation-info .info-row span {
  color: #2c3e50;
}

/* 批量关联和AI关联 */
.batch-relation-container,
.ai-relation-container {
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
}

.batch-header,
.ai-header {
  margin-bottom: 40px;
}

.batch-header h2,
.ai-header h2 {
  margin: 0 0 10px 0;
  color: #2c3e50;
  font-size: 28px;
}

.batch-header p,
.ai-header p {
  color: #7f8c8d;
  font-size: 16px;
  margin: 0;
}

.batch-content,
.ai-content {
  padding: 60px 20px;
}

.batch-info,
.ai-info {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  font-size: 16px;
  color: #7f8c8d;
}

.batch-info i,
.ai-info i {
  font-size: 24px;
  color: #667eea;
}

/* 批量关联样式 */
.batch-relation-container,
.ai-relation-container {
  max-width: 1200px;
  margin: 0 auto;
}

.batch-header,
.ai-header {
  text-align: center;
  margin-bottom: 40px;
}

.batch-header h2,
.ai-header h2 {
  margin: 0 0 10px 0;
  color: #2c3e50;
  font-size: 28px;
}

.batch-header p,
.ai-header p {
  color: #7f8c8d;
  font-size: 16px;
  margin: 0;
}

.batch-mode-selection {
  margin-bottom: 40px;
}

.batch-mode-selection h3 {
  margin: 0 0 20px 0;
  color: #2c3e50;
  font-size: 20px;
}

.mode-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
}

.mode-option {
  border: 2px solid #e8ecef;
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s;
  background: white;
}

.mode-option:hover {
  border-color: #667eea;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.1);
}

.mode-option.active {
  border-color: #667eea;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
}

.mode-option input[type="radio"] {
  display: none;
}

.option-content {
  text-align: center;
}

.option-content i {
  font-size: 32px;
  color: #667eea;
  margin-bottom: 15px;
  display: block;
}

.option-title {
  display: block;
  font-size: 18px;
  font-weight: bold;
  color: #2c3e50;
  margin-bottom: 8px;
}

.option-desc {
  display: block;
  font-size: 14px;
  color: #7f8c8d;
  line-height: 1.4;
}

/* 智能匹配模式样式 */
.auto-settings {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 25px;
  margin-bottom: 30px;
}

.auto-settings h4 {
  margin: 0 0 20px 0;
  color: #2c3e50;
  font-size: 18px;
}

.settings-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 25px;
}

.setting-item {
  background: white;
  border-radius: 8px;
  padding: 15px;
  border: 1px solid #e8ecef;
}

.setting-item label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: bold;
  color: #2c3e50;
  margin-bottom: 10px;
}

.sub-setting {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  font-size: 14px;
}

.sub-setting input,
.sub-setting select {
  padding: 4px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.auto-actions {
  display: flex;
  gap: 15px;
  justify-content: center;
}

/* 匹配结果样式 */
.match-results {
  background: white;
  border-radius: 12px;
  padding: 25px;
  border: 1px solid #e8ecef;
}

.match-results h4 {
  margin: 0 0 20px 0;
  color: #2c3e50;
  font-size: 18px;
}

.results-list {
  margin-bottom: 25px;
}

.match-result-item,
.ai-result-item {
  border: 1px solid #e8ecef;
  border-radius: 8px;
  margin-bottom: 15px;
  overflow: hidden;
  background: white;
}

.result-header {
  background: #f8f9fa;
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.result-checkbox {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.confidence-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: bold;
}

.confidence-badge.high {
  background: #d4edda;
  color: #155724;
}

.confidence-badge.medium {
  background: #fff3cd;
  color: #856404;
}

.confidence-badge.low {
  background: #f8d7da;
  color: #721c24;
}

.result-actions {
  display: flex;
  gap: 8px;
}

.result-content {
  padding: 20px;
}

.match-pair,
.ai-match-pair {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
}

.match-source,
.match-target {
  flex: 1;
  text-align: center;
}

.entity-type {
  font-size: 12px;
  color: #7f8c8d;
  margin-bottom: 5px;
}

.entity-info {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.entity-id {
  font-weight: bold;
  color: #2c3e50;
  font-size: 14px;
}

.entity-amount {
  color: #27ae60;
  font-weight: bold;
}

.entity-date {
  font-size: 12px;
  color: #7f8c8d;
}

.match-arrow {
  margin: 0 20px;
  color: #667eea;
  font-size: 18px;
}

.match-reasons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.reason-tag {
  background: #e3f2fd;
  color: #1976d2;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
}

.batch-actions {
  display: flex;
  gap: 15px;
  justify-content: center;
}

/* AI智能关联样式 */
.ai-config-section,
.ai-control-section,
.ai-results-section,
.ai-feedback-section {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 25px;
  margin-bottom: 30px;
  border: 1px solid #e8ecef;
}

.ai-config-section h3,
.ai-control-section h3,
.ai-results-section h3,
.ai-feedback-section h3 {
  margin: 0 0 20px 0;
  color: #2c3e50;
  font-size: 18px;
}

.config-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.config-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.config-item label {
  font-weight: bold;
  color: #2c3e50;
  font-size: 14px;
}

.config-select {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  background: white;
}

.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.checkbox-option {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.control-panel {
  background: white;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e8ecef;
}

.analysis-stats {
  display: flex;
  justify-content: space-around;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e8ecef;
}

.stat-item {
  text-align: center;
}

.stat-label {
  display: block;
  font-size: 12px;
  color: #7f8c8d;
  margin-bottom: 5px;
}

.stat-value {
  display: block;
  font-size: 18px;
  font-weight: bold;
  color: #2c3e50;
}

.control-actions {
  display: flex;
  gap: 15px;
  justify-content: center;
}

.progress-section {
  margin-top: 20px;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #e8ecef;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 10px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  transition: width 0.3s;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  color: #7f8c8d;
}

.results-summary {
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  border: 1px solid #e8ecef;
}

.summary-stats {
  display: flex;
  justify-content: space-around;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e8ecef;
}

.summary-item {
  text-align: center;
}

.summary-label {
  display: block;
  font-size: 12px;
  color: #7f8c8d;
  margin-bottom: 5px;
}

.summary-value {
  display: block;
  font-size: 16px;
  font-weight: bold;
  color: #2c3e50;
}

.results-actions {
  display: flex;
  gap: 15px;
  justify-content: center;
}

.ai-analysis {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
  margin-top: 15px;
}

.analysis-reasons h5,
.analysis-suggestion h5 {
  margin: 0 0 10px 0;
  color: #2c3e50;
  font-size: 14px;
}

.analysis-reasons ul {
  margin: 0;
  padding-left: 20px;
  color: #5a6c7d;
}

.analysis-suggestion p {
  margin: 0;
  color: #5a6c7d;
  font-style: italic;
}

.feedback-info {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  padding: 15px;
  background: #e3f2fd;
  border-radius: 8px;
  color: #1976d2;
}

.feedback-stats {
  display: flex;
  justify-content: space-around;
  background: white;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e8ecef;
}

.feedback-item {
  text-align: center;
}

.feedback-label {
  display: block;
  font-size: 12px;
  color: #7f8c8d;
  margin-bottom: 5px;
}

.feedback-value {
  display: block;
  font-size: 16px;
  font-weight: bold;
  color: #2c3e50;
}

/* 手动选择和文件导入样式 */
.manual-info,
.import-info {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  padding: 15px;
  background: #fff3cd;
  border-radius: 8px;
  color: #856404;
}

.manual-selection {
  background: white;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e8ecef;
}

.selection-summary {
  display: flex;
  justify-content: space-around;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e8ecef;
}

.summary-item {
  text-align: center;
}

.summary-label {
  display: block;
  font-size: 12px;
  color: #7f8c8d;
  margin-bottom: 5px;
}

.summary-count {
  display: block;
  font-size: 18px;
  font-weight: bold;
  color: #667eea;
}

.manual-actions,
.import-actions {
  display: flex;
  gap: 15px;
  justify-content: center;
}

.import-results {
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-top: 20px;
  border: 1px solid #e8ecef;
}

.import-results h4 {
  margin: 0 0 15px 0;
  color: #2c3e50;
}

.results-summary {
  display: flex;
  gap: 20px;
  margin-bottom: 15px;
}

.success-count {
  color: #28a745;
  font-weight: bold;
}

.error-count {
  color: #dc3545;
  font-weight: bold;
}

.results-details {
  max-height: 200px;
  overflow-y: auto;
}

.result-item {
  padding: 8px 12px;
  margin-bottom: 5px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.result-item.success {
  background: #d4edda;
  color: #155724;
}

.result-item.error {
  background: #f8d7da;
  color: #721c24;
}

.result-index {
  font-weight: bold;
  min-width: 30px;
}

/* 分页样式 */
.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 30px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e8ecef;
}

.pagination-info {
  display: flex;
  align-items: center;
  gap: 20px;
  color: #5a6c7d;
  font-size: 14px;
}

.page-size-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-size-select {
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  cursor: pointer;
}

.page-size-select:focus {
  outline: none;
  border-color: #667eea;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-btn {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: white;
  color: #5a6c7d;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pagination-btn:hover:not(:disabled) {
  background: #667eea;
  color: white;
  border-color: #667eea;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  gap: 4px;
}

.page-number {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: white;
  color: #5a6c7d;
  cursor: pointer;
  transition: all 0.3s;
  min-width: 40px;
  text-align: center;
  font-size: 14px;
}

.page-number:hover {
  background: #667eea;
  color: white;
  border-color: #667eea;
}

.page-number.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-color: #667eea;
  font-weight: bold;
}

/* 浮窗样式 */
.image-modal, .detail-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.modal-content {
  background: white;
  border-radius: 12px;
  max-width: 85vw;
  max-height: 85vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalFadeIn 0.3s ease-out;
}

.modal-content.large {
  max-width: 1000px;
  width: 85vw;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 28px;
  border-bottom: 1px solid #e8ecef;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.modal-header h4 {
  margin: 0;
  color: white;
  font-size: 18px;
  font-weight: 600;
}

.close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: white;
  padding: 8px;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.modal-body {
  padding: 28px;
  overflow-y: auto;
  flex: 1;
  background: #fafbfc;
}

.modal-footer {
  padding: 20px 28px;
  border-top: 1px solid #e8ecef;
  background: white;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

/* 图片预览 */
.preview-image {
  max-width: 100%;
  max-height: 65vh;
  object-fit: contain;
  display: block;
  margin: 0 auto;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

/* 详情内容 */
.detail-content {
  max-height: 55vh;
  overflow-y: auto;
  padding-right: 8px;
}

.detail-content::-webkit-scrollbar {
  width: 6px;
}

.detail-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.detail-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.detail-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.detail-section {
  margin-bottom: 32px;
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.detail-section h5 {
  margin: 0 0 18px 0;
  color: #2c3e50;
  font-size: 16px;
  font-weight: 600;
  border-bottom: 2px solid #667eea;
  padding-bottom: 8px;
  position: relative;
}

.detail-section h5::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 30px;
  height: 2px;
  background: #764ba2;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
  border-left: 3px solid #667eea;
}

.detail-item.full-width {
  grid-column: 1 / -1;
}

.detail-item label {
  font-weight: 600;
  color: #5a6c7d;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-item span {
  color: #2c3e50;
  font-size: 14px;
  font-weight: 500;
}

.detail-item span.amount {
  color: #27ae60;
  font-weight: bold;
  font-size: 18px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    gap: 15px;
    align-items: flex-start;
  }

  .header-stats {
    flex-wrap: wrap;
  }

  .tab-container {
    flex-direction: column;
  }

  .tab-button {
    border-bottom: none;
    border-right: 3px solid transparent;
  }

  .tab-button.active {
    border-right-color: #764ba2;
  }

  .items-grid, .relations-grid {
    grid-template-columns: 1fr;
  }

  .search-controls {
    flex-direction: column;
    align-items: stretch;
  }

  .modal-content {
    margin: 10px;
    max-width: calc(100vw - 20px);
    max-height: calc(100vh - 20px);
    border-radius: 8px;
  }

  .modal-header {
    padding: 16px 20px;
  }

  .modal-body {
    padding: 20px;
  }

  .modal-footer {
    padding: 16px 20px;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }

  .relation-entities {
    flex-direction: column;
    gap: 15px;
  }

  .relation-arrow {
    transform: rotate(90deg);
    margin: 10px 0;
  }

  .pagination-container {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }

  .pagination-info {
    justify-content: center;
    flex-wrap: wrap;
  }

  .pagination-controls {
    justify-content: center;
  }

  .page-numbers {
    flex-wrap: wrap;
    justify-content: center;
  }
}

/* 组合卡片样式 */
.receipt-group-card, .bill-group-card {
  border-left: 4px solid #f39c12;
}

.receipt-group-card .card-header .item-title .item-number,
.bill-group-card .card-header .item-title .item-number {
  color: #f39c12;
  font-weight: bold;
}

.receipt-group-card .card-header .item-title .item-number::before {
  content: "📁 ";
  margin-right: 4px;
}

.bill-group-card .card-header .item-title .item-number::before {
  content: "📂 ";
  margin-right: 4px;
}

.status-active {
  color: #27ae60;
  font-weight: bold;
  background: rgba(39, 174, 96, 0.1);
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
}

/* 选择模式样式 */
.selection-mode {
  display: flex;
  gap: 15px;
  align-items: center;
}

.radio-option {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 14px;
  color: #5a6c7d;
  transition: color 0.2s ease;
}

.radio-option:hover {
  color: #667eea;
}

.radio-option input[type="radio"] {
  margin: 0;
  cursor: pointer;
}

.radio-option input[type="radio"]:checked + span {
  color: #667eea;
  font-weight: 600;
}

/* 实体详情样式 */
.entity-card {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: white;
  overflow: hidden;
  margin-bottom: 16px;
}

.entity-header {
  background: #f8f9fa;
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e9ecef;
}

.entity-type {
  background: #007bff;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}

.entity-id {
  color: #666;
  font-size: 12px;
  font-family: monospace;
}

.entity-details {
  padding: 16px;
}

.detail-row {
  display: flex;
  margin-bottom: 8px;
  align-items: flex-start;
}

.detail-row label {
  min-width: 80px;
  font-weight: 600;
  color: #666;
  margin-right: 12px;
  font-size: 13px;
}

.detail-row span {
  color: #333;
  font-size: 13px;
  flex: 1;
}

.detail-row .amount {
  color: #28a745;
  font-weight: 600;
  font-family: monospace;
}

.loading-text {
  padding: 16px;
  text-align: center;
  color: #666;
  font-style: italic;
}

.error-text {
  padding: 16px;
  text-align: center;
  color: #dc3545;
  background: #f8d7da;
  border-radius: 4px;
  margin: 8px;
}
</style>
</script>
